import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Building } from "lucide-react";
import { usePerkOptOuts } from "@/hooks/usePerks";
import { format } from "date-fns";

interface OptOutsTabProps {
  perkId: string;
}

export function OptOutsTab({ perkId }: OptOutsTabProps) {
  const { data: optOuts = [], isLoading, error } = usePerkOptOuts(perkId);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error loading opt-outs</AlertTitle>
        <AlertDescription>Failed to load opt-out data. Please try refreshing.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge variant="outline">
          <Building className="w-3 h-3 mr-1" />
          {optOuts.length} organization{optOuts.length !== 1 ? "s" : ""} opted out
        </Badge>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : optOuts.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No organizations have opted out of this perk.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Opted Out At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {optOuts.map((optOut) => (
                  <TableRow key={optOut.organizationId}>
                    <TableCell className="font-medium">{optOut.organizationName}</TableCell>
                    <TableCell>
                      {optOut.optedOutAt
                        ? format(new Date(optOut.optedOutAt), "MMM d, yyyy HH:mm")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
